import {
  Button,
  Stepper,
  Label,
  SingleSelect,
  Banner,
  BannerItem,
} from "@/vendor/Forge/dist";
import { useState, useEffect } from "react";
import { styled } from "@stitches/react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  MultiselectChangeEvent,
  StructuredOption,
  RadioGroup,
} from "@/vendor/Forge/dist";
import TaoSelector from "./selector";
import TaoConfirmation from "./confirm";
import BucketAssignment from "./bucket-assignment";
import { useCreateTao } from "@/hooks/use-create-tao";
import {
  TaoDepartmentsList,
  TaoOrderTypesList,
  TaoProvidersList,
} from "@/types/tao-routing";
import { useLocation } from "react-router-dom";
import { TaoList, IndividualTaoDataResponse } from "@/types/tao-routing";
import { useUpdateTao } from "@/hooks/use-update-tao";
import Loader from "../Loader";
import { AxiosError } from "axios";

const SelectionFieldWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",

  "& .fe_c_multiselect__control": {
    width: "350px",
  },
  "& .fe_c_input .fe_c_input__input": {
    maxWidth: "150px",
    height: "34px",
  },
});

const SelectorWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
});

const RadioGroupWrapper = styled("div", {
  display: "flex",
  flexFlow: "column",
  marginBottom: "15px",
  marginLeft: "15px",

  "& .fe_c_radiogroup": {
    display: "flex",
    flexFlow: "row",
    gap: "15px",
  },
});

const ContentWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  marginBottom: "25px",
  marginTop: "25px",
  padding: "10px",
});

const TaoEditFailureWrapper = styled("div", {
  marginBottom: "15px",

  "& .banner": {
    width: "100%",
    top: "1px",
    left: "-2px",
    gap: "0px",
    opacity: "0px",
    position: "relative",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    textAlign: "left",
    fontSize: "18px",
    color: "#118647",
    fontFamily: "Source Sans Pro",
    border: "0px 0px 100px 8px",
    borderRadius: "4px",
  },
});

const ButtonsWrapper = styled("div", {
  display: "flex",
  justifyContent: "space-between",
});

const StepperButtons = styled("div", {
  display: "flex",

  "& .fe_c_button": {
    marginLeft: "10px",
  },
});

const steps = [
  "Departments",
  "Provider",
  "Genus, Order Type",
  "Assign to Bucket",
  "Confirm",
];

const stepMap = ["departments", "providers", "genus", "bucket", "confirm"];

interface Props {
  originalList: {
    departments: string[];
    providers: string[];
    genus: string[];
    buckets: string[];
    orderTypes: string[];
  };
  fullList: {
    departments: TaoDepartmentsList[];
    providers: TaoProvidersList[];
    orderTypes: TaoOrderTypesList[];
  };
  selectedTaoDataFromBackend?: IndividualTaoDataResponse;
}

interface SelectedTaoList {
  selectedTaoData: TaoList[];
  isDuplicate?: boolean;
  isEdit?: boolean;
}

//Todo: selectall pushes 'all', pagination

const TaoEditorContent = ({
  originalList,
  fullList,
  selectedTaoDataFromBackend,
}: Props) => {
  const { selectedTaoData, isEdit, isDuplicate } =
    (useLocation().state as SelectedTaoList) ?? [];
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(
    searchParams.get("currentStep") as string,
  );
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>(
    [],
  );
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [selectedGenus, setSelectedGenus] = useState<string[]>([]);
  const [selectedBucket, setSelectedBucket] = useState<string[]>([]);
  const [selectedOrderTypes, setSelectedOrderTypes] = useState<string[]>([]);
  const [selectedOrderTypeIds, setSelectedOrderTypeIds] = useState<string[]>(
    [],
  );
  const [priority, setPriority] = useState<string>("100");
  const [allOrSelectorDepartments, setAllOrSelectorDepartments] =
    useState<string>();
  const [allOrSelectorProviders, setAllOrSelectorProviders] =
    useState<string>();
  const [allOrSelectorGenus, setAllOrSelectorGenus] = useState<string>();
  const [disableEditTao, setDisableEditTao] = useState(true);
  const taoRuleProperties = {
    departments: selectedDepartments,
    departmentIds: selectedDepartmentIds,
    providers: selectedProviders,
    providerIds: selectedProviderIds,
    orderGenusList: selectedGenus,
    bucketName: selectedBucket[0],
    orderTypes: selectedOrderTypes,
    orderTypeIds: selectedOrderTypeIds,
    ordering: priority,
  };
  const [createTaoError, setCreateTaoError] = useState<string>();
  const [updateTaoError, setUpdateTaoError] = useState<string>();
  const { createTao, isPending } = useCreateTao({});
  const { updateTao } = useUpdateTao(
    {},
    selectedTaoData ? selectedTaoData[0]?.taoId : undefined,
  );

  const handleEditTao = () => {
    //Check if the selectedTaoDatafromBE != selected
    updateTao(taoRuleProperties)
      .then(() => {
        navigate("/tao-routing/");
      })
      .catch((err: AxiosError) => {
        // do as you wish with the error
        switch (err.response?.status) {
          case 400:
            setUpdateTaoError(
              "A TAO cannot be created with selected parameters.",
            );
            break;
          case 409:
            setUpdateTaoError("A TAO already exists with selected parameters.");
            break;
          case 500:
            setUpdateTaoError("An internal server error has occurred.");
            break;
          default:
            break;
        }
      });
  };

  useEffect(() => {
    if (!selectedTaoDataFromBackend) {
      setAllOrSelectorDepartments("All");
      setAllOrSelectorProviders("All");
      setAllOrSelectorGenus("All");
    }
  }, [selectedTaoDataFromBackend]);

  useEffect(() => {
    if (allOrSelectorDepartments == "All") {
      setSelectedDepartments(["ALL"]);
      setSelectedDepartmentIds(["ALL"]);
    }
    if (allOrSelectorProviders == "All") {
      setSelectedProviders(["ALL"]);
      setSelectedProviderIds(["ALL"]);
    }
    if (allOrSelectorGenus == "All") {
      setSelectedGenus(["ALL"]);
    }
  }, [allOrSelectorDepartments, allOrSelectorGenus, allOrSelectorProviders]);

  useEffect(() => {
    setSelectedDepartments([]);
  }, [selectedTaoData]);

  useEffect(() => {
    if (selectedTaoData) {
      setAllOrSelectorGenus("Selected");
      setSelectedGenus(selectedTaoData[0].orderGenusList ?? []);
      if (selectedTaoData[0].orderGenusList[0] === "ALL") {
        setAllOrSelectorGenus("All");
        setSelectedGenus(["ALL"]);
      }
    }
  }, [selectedTaoData]);

  useEffect(() => {
    if (selectedTaoData) {
      setSelectedBucket([selectedTaoData[0].bucketName]);
    }
  }, [selectedTaoData]);

  useEffect(() => {
    if (selectedTaoData) {
      const orderingToString = selectedTaoData[0].ordering.toString();
      setPriority(orderingToString);
    }
  }, [selectedTaoData]);

  useEffect(() => {
    if (selectedTaoData) {
      setSelectedOrderTypes(selectedTaoData[0].orderTypes);
    }
  }, [selectedTaoData]);

  useEffect(() => {
    let newSelectedDepartments = fullList.departments
      .filter((department) => selectedDepartments.includes(department.name))
      .map((department) => department.departmentId);

    if (
      newSelectedDepartments.length === 0 &&
      selectedTaoDataFromBackend?.departmentMap.length === 0
    ) {
      newSelectedDepartments = ["ALL"];
    }

    if (allOrSelectorDepartments == "All") {
      setSelectedDepartmentIds(["ALL"]);
    } else {
      setSelectedDepartmentIds(
        Array.from(new Set([...newSelectedDepartments])),
      );
    }
  }, [
    selectedDepartments,
    fullList.departments,
    selectedTaoData,
    selectedTaoDataFromBackend,
    allOrSelectorDepartments,
  ]);

  useEffect(() => {
    if (selectedTaoDataFromBackend) {
      if (selectedTaoDataFromBackend.departmentMap.length === 0) {
        setAllOrSelectorDepartments("All");
        setSelectedDepartments(["ALL"]);
      } else {
        setAllOrSelectorDepartments("Selected");
        setSelectedDepartments((prevSelected) => {
          const departSet = new Set([
            ...prevSelected,
            ...(selectedTaoDataFromBackend?.departmentMap.map(
              (department) => department.name,
            ) ?? []),
          ]);
          return Array.from(departSet);
        });
      }

      if (selectedTaoDataFromBackend.providerMap.length === 0) {
        setAllOrSelectorProviders("All");
        setSelectedProviders(["ALL"]);
      } else {
        setAllOrSelectorProviders("Selected");
        setSelectedProviders((prevSelected) => {
          const providerSet = new Set([
            ...prevSelected,
            ...(selectedTaoDataFromBackend?.providerMap.map(
              (provider) => provider.displayName,
            ) ?? []),
          ]);
          return Array.from(providerSet);
        });
      }
    }
  }, [selectedTaoDataFromBackend]);

  useEffect(() => {
    if (allOrSelectorProviders == "All") {
      setSelectedProviderIds(["ALL"]);
    } else {
      setSelectedProviderIds(
        fullList.providers
          .filter((provider) =>
            selectedProviders.includes(provider.displayName),
          )
          .map((provider) => provider.providerId),
      );
    }
  }, [selectedProviders, fullList.providers, allOrSelectorProviders]);

  useEffect(() => {
    setSelectedOrderTypeIds(
      fullList.orderTypes
        .filter((orderType) =>
          selectedOrderTypes.includes(orderType.orderTypeName),
        )
        .map((orderType) => orderType.orderTypeId),
    );
  }, [selectedOrderTypes, fullList.orderTypes]);

  const stateMapper = {
    departments: {
      state: selectedDepartments,
      setter: setSelectedDepartments,
    },
    providers: {
      state: selectedProviders,
      setter: setSelectedProviders,
    },
    genus: {
      state: selectedGenus,
      setter: setSelectedGenus,
    },
    bucket: {
      state: selectedBucket,
      setter: setSelectedBucket,
    },
    orderType: {
      state: selectedOrderTypes,
      setter: setSelectedOrderTypes,
    },
  };

  const navigate = useNavigate();

  useEffect(() => {
    setCurrentStep(searchParams.get("currentStep") as string);
  }, [currentStep, navigate, searchParams]);

  useEffect(() => {
    if (
      selectedBucket.length > 0 &&
      selectedDepartments.length > 0 &&
      selectedProviders.length > 0 &&
      selectedGenus.length > 0 &&
      selectedOrderTypes.length > 0
    ) {
      const providersIdsFromMap =
        selectedTaoDataFromBackend?.taoRulesResponse.providerIds ?? [];
      const providersFromMap =
        selectedTaoDataFromBackend?.taoRulesResponse.providers ?? [];
      const orderTypesFromMap =
        selectedTaoDataFromBackend?.taoRulesResponse.orderTypes ?? [];
      const orderTypesIdsFromMap =
        selectedTaoDataFromBackend?.taoRulesResponse.orderTypeIds ?? [];
      const orderGenusFromMap =
        selectedTaoDataFromBackend?.taoRulesResponse.orderGenusList ?? [];
      const departmentNamesFromMap =
        selectedTaoDataFromBackend?.taoRulesResponse.departments ?? [];
      const departmentIdsFromMap =
        selectedTaoDataFromBackend?.taoRulesResponse.departmentIds ?? [];

      //compare the bucket from selectedTaoDataFromBackend with selectedBucket from form
      if (
        !(
          typeof selectedBucket[0] !== "undefined" &&
          selectedBucket[0] ===
            selectedTaoDataFromBackend?.taoRulesResponse.bucketName
        )
      ) {
        setDisableEditTao(false);
        return;
      }

      //Compare orderTypes
      if (!areStringArraysEqual(selectedOrderTypes, orderTypesFromMap)) {
        setDisableEditTao(false);
        return;
      }

      //Compare orderTypeIds
      if (!areStringArraysEqual(selectedOrderTypeIds, orderTypesIdsFromMap)) {
        setDisableEditTao(false);
        return;
      }

      //compare the orderGenusList from selectedTaoDataFromBackend with selectedOrderGenus
      if (!areStringArraysEqual(selectedGenus, orderGenusFromMap)) {
        setDisableEditTao(false);
        return;
      }

      //Extract departments from selectedTaoDataFromBackend
      if (!areStringArraysEqual(selectedDepartments, departmentNamesFromMap)) {
        setDisableEditTao(false);
        return;
      }

      //Extract departmentIds from selectedTaoDataFromBackend
      if (!areStringArraysEqual(selectedDepartmentIds, departmentIdsFromMap)) {
        setDisableEditTao(false);
        return;
      }

      //Extract providers from selectedTaoDataFromBackend
      if (!areStringArraysEqual(selectedProviders, providersFromMap)) {
        setDisableEditTao(false);
        return;
      }

      //Extract providerIds from selectedTaoDataFromBackend
      if (!areStringArraysEqual(selectedProviderIds, providersIdsFromMap)) {
        setDisableEditTao(false);
        return;
      }

      if (
        !(
          typeof priority !== "undefined" &&
          priority ===
            selectedTaoDataFromBackend?.taoRulesResponse.ordering.toString()
        )
      ) {
        setDisableEditTao(false);
        return;
      } else {
        setDisableEditTao(true);
        return;
      }
    }
  }, [
    selectedBucket,
    selectedDepartmentIds,
    selectedDepartments,
    selectedGenus,
    selectedOrderTypeIds,
    selectedOrderTypes,
    selectedProviderIds,
    selectedProviders,
    selectedTaoData,
    priority,
    selectedTaoDataFromBackend,
  ]);

  const handleNextClick = () => {
    const nextIdx = stepMap.indexOf(currentStep) + 1;
    if (stepMap.indexOf(currentStep) < 4) {
      setCurrentStep(stepMap[nextIdx]);
      navigate(`/tao-routing/tao-editor?currentStep=${stepMap[nextIdx]}`, {
        state: { selectedTaoData: selectedTaoData, isEdit, isDuplicate },
      });
    }
  };

  const handleBackClick = () => {
    const nextIdx = stepMap.indexOf(currentStep) - 1;
    if (stepMap.indexOf(currentStep) > 0) {
      setCurrentStep(stepMap[nextIdx]);
      navigate(`/tao-routing/tao-editor?currentStep=${stepMap[nextIdx]}`, {
        state: { selectedTaoData: selectedTaoData, isEdit, isDuplicate },
      });
    }
  };

  const handleCreateNewTao = () => {
    createTao(taoRuleProperties)
      .then(() => {
        navigate("/tao-routing/");
      })
      .catch((err: AxiosError) => {
        // do as you wish with the error
        switch (err.response?.status) {
          case 400:
            setCreateTaoError(
              "A TAO cannot be created with selected parameters.",
            );
            break;
          case 409:
            setCreateTaoError("A TAO already exists with selected parameters.");
            break;
          case 500:
            setCreateTaoError("An internal server error has occurred.");
            break;
          default:
            break;
        }
      });
  };

  const areStringArraysEqual = (arr1: string[], arr2: string[]): boolean => {
    if (arr1.length !== arr2.length) {
      return false;
    }
    const sortedArr1 = [...arr1].map((elm) => String(elm)).sort();
    const sortedArr2 = [...arr2].map((elm) => String(elm)).sort();
    return sortedArr1.every((value) => sortedArr2.includes(value));
  };
  return (
    <Loader displayText="Loading..." isLoading={isPending}>
      <ContentWrapper>
        <Stepper
          navigableSteps={true}
          selected={stepMap.indexOf(currentStep)}
          steps={steps}
          onSelect={(idx) => {
            navigate(`/tao-routing/tao-editor?currentStep=${stepMap[idx]}`, {
              state: { selectedTaoData: selectedTaoData, isEdit, isDuplicate },
            });
          }}
        />
        {disableEditTao && currentStep == "confirm" && isEdit ? (
          <TaoEditFailureWrapper>
            <Banner alertType="critical">
              <BannerItem headerText="No changes made, unable to save TAO">
                <p>
                  There is already an existing TAO {selectedTaoData[0]?.taoId}{" "}
                  with these parameters. Please update fields below to edit TAO.
                </p>
              </BannerItem>
            </Banner>
          </TaoEditFailureWrapper>
        ) : (
          <></>
        )}
        {createTaoError && currentStep == "confirm" ? (
          <TaoEditFailureWrapper>
            <Banner alertType="critical">
              <BannerItem headerText="Unable to create TAO">
                <p>{createTaoError}</p>
              </BannerItem>
            </Banner>
          </TaoEditFailureWrapper>
        ) : (
          <></>
        )}
        {updateTaoError && currentStep == "confirm" ? (
          <TaoEditFailureWrapper>
            <Banner alertType="critical">
              <BannerItem
                headerText={`Unable to update TAO ${selectedTaoData[0]?.taoId}`}
              >
                <p>{updateTaoError}</p>
              </BannerItem>
            </Banner>
          </TaoEditFailureWrapper>
        ) : (
          <></>
        )}
        {currentStep == "confirm" ? (
          <TaoConfirmation
            selectedDepartments={selectedDepartments}
            selectedProviders={selectedProviders}
            selectedGenus={selectedGenus}
            selectedBucket={selectedBucket}
            selectedOrderTypes={selectedOrderTypes}
            priority={priority}
          />
        ) : currentStep == "bucket" ? (
          <BucketAssignment
            bucketList={originalList.buckets}
            setBuckets={setSelectedBucket}
            selectedBucket={selectedBucket}
            priority={priority}
            setPriority={setPriority}
          />
        ) : (
          <SelectorWrapper>
            {currentStep == "departments" ? (
              <RadioGroupWrapper>
                <Label
                  aria-label="allOrSelectorDepartments"
                  text="Selected Departments"
                />
                <RadioGroup
                  id="allOrSelector"
                  value={allOrSelectorDepartments}
                  options={["All", "Selected"]}
                  onChange={(e) => {
                    setAllOrSelectorDepartments(e.target.value);
                    setSelectedDepartments([]);
                    setSelectedDepartmentIds([]);
                  }}
                />
              </RadioGroupWrapper>
            ) : currentStep == "providers" ? (
              <RadioGroupWrapper>
                <Label
                  aria-label="allOrSelectorProviders"
                  text="Selected Providers"
                />
                <RadioGroup
                  id="allOrSelector"
                  value={allOrSelectorProviders}
                  options={["All", "Selected"]}
                  onChange={(e) => {
                    setAllOrSelectorProviders(e.target.value);
                    setSelectedProviders([]);
                    setSelectedProviderIds([]);
                  }}
                />
              </RadioGroupWrapper>
            ) : currentStep == "genus" ? (
              <RadioGroupWrapper>
                <Label aria-label="allOrSelectorGenus" text="Selected Genus" />
                <RadioGroup
                  id="allOrSelector"
                  value={allOrSelectorGenus}
                  options={["All", "Selected"]}
                  onChange={(e) => {
                    setAllOrSelectorGenus(e.target.value);
                    setSelectedGenus([]);
                  }}
                />
              </RadioGroupWrapper>
            ) : (
              <></>
            )}
            {currentStep == "departments" &&
            allOrSelectorDepartments == "Selected" ? (
              <TaoSelector
                originalList={
                  originalList[currentStep as keyof typeof originalList]
                }
                selectedValues={
                  stateMapper[currentStep as keyof typeof stateMapper].state
                }
                setSelectedValues={
                  stateMapper[currentStep as keyof typeof stateMapper].setter
                }
              />
            ) : currentStep == "providers" &&
              allOrSelectorProviders == "Selected" ? (
              <TaoSelector
                originalList={
                  originalList[currentStep as keyof typeof originalList]
                }
                selectedValues={
                  stateMapper[currentStep as keyof typeof stateMapper].state
                }
                setSelectedValues={
                  stateMapper[currentStep as keyof typeof stateMapper].setter
                }
              />
            ) : currentStep == "genus" && allOrSelectorGenus == "Selected" ? (
              <TaoSelector
                originalList={
                  originalList[currentStep as keyof typeof originalList]
                }
                selectedValues={
                  stateMapper[currentStep as keyof typeof stateMapper].state
                }
                setSelectedValues={
                  stateMapper[currentStep as keyof typeof stateMapper].setter
                }
              />
            ) : (
              <></>
            )}
          </SelectorWrapper>
        )}
        {currentStep == "genus" ? (
          <SelectionFieldWrapper>
            <Label text="Order Type" />
            <SingleSelect
              options={originalList.orderTypes}
              onChange={(
                event: MultiselectChangeEvent<StructuredOption, false>,
              ) => {
                const selected = event.target.value;
                return setSelectedOrderTypes([selected?.value as string]);
              }}
              value={selectedOrderTypes[0]}
            />
          </SelectionFieldWrapper>
        ) : (
          <></>
        )}
        <ButtonsWrapper>
          <Link to="/tao-routing/">
            <Button variant="secondary" data-testid="cancelButton">
              Cancel
            </Button>
          </Link>
          <StepperButtons>
            <Button onClick={handleBackClick} variant="secondary">
              Back
            </Button>
            {currentStep == "confirm" ? (
              isEdit ? (
                <Button disabled={disableEditTao} onClick={handleEditTao}>
                  Save Changes
                </Button>
              ) : (
                <Button onClick={handleCreateNewTao}>Create Tao</Button>
              )
            ) : (
              <Button onClick={handleNextClick}>Next</Button>
            )}
          </StepperButtons>
        </ButtonsWrapper>
      </ContentWrapper>
    </Loader>
  );
};

export default TaoEditorContent;
